/**
 * Embeddable DMS section — used inside the Asset Details and Device Details
 * pages to surface "documents linked to this asset/device" without leaving the
 * detail page. Uploads default to a sensible folder path so the file lands in
 * the right place in the DMS automatically.
 */
import { useCallback, useEffect, useState } from "react";
import {
  ExternalLink,
  File as FileGenericIcon,
  FileCode2,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import {
  deleteDocument,
  listDocuments,
  type DMSDocument,
  type DMSDocumentType,
  type LinkableEntityType,
} from "../../lib/dmsApi";
import { UploadDocumentModal } from "./UploadDocumentModal";
import { FileViewer } from "./FileViewer";

interface DMSDocumentsPanelProps {
  virtualSiteId: string;
  entityType: LinkableEntityType;
  entityId: string;
  /** Pre-set the upload folder path, e.g. "Assets/PUMP-01 - Pump 17". */
  defaultFolderPath?: string;
  /** Heading shown above the panel. */
  title?: string;
}

const TYPE_ICONS: Record<DMSDocumentType, typeof FileText> = {
  pdf: FileText,
  image: ImageIcon,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  json: FileCode2,
  xml: FileCode2,
  text: FileText,
  docx: FileText,
  other: FileGenericIcon,
};


export function DMSDocumentsPanel({
  virtualSiteId,
  entityType,
  entityId,
  defaultFolderPath,
  title = "DMS Documents",
}: DMSDocumentsPanelProps) {
  const [docs, setDocs] = useState<DMSDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewing, setViewing] = useState<DMSDocument | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DMSDocument | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await listDocuments({
        virtual_site_id: virtualSiteId,
        linked_entity_type: entityType,
        linked_entity_id: entityId,
        include_subfolders: true,
      });
      setDocs(items);
    } finally {
      setLoading(false);
    }
  }, [virtualSiteId, entityType, entityId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteDocument(confirmDelete.id);
    setConfirmDelete(null);
    if (viewing?.id === confirmDelete.id) setViewing(null);
    await reload();
  };

  const viewerVersion = viewing
    ? viewing.versions.find((v) => v.is_current && v.status === "ready")
      ?? viewing.versions.find((v) => v.status === "ready")
      ?? viewing.versions[0]
    : null;

  return (
    <section className="rounded-xl border border-white/10 bg-surface-900/40">
      <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-surface-100">{title}</h3>
          <p className="text-xs text-surface-500">
            Files in the DMS linked to this {entityType.replace("_", " ")}
            {defaultFolderPath ? ` · default folder: ${defaultFolderPath}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/dms"
            className="inline-flex items-center gap-1.5 text-xs text-surface-300 hover:text-surface-100"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open DMS
          </Link>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4" /> Upload
          </Button>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="p-4 text-sm text-surface-400 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : docs.length === 0 ? (
            <div className="p-6 text-sm text-surface-400 text-center">
              No documents linked yet. Upload one — it will be auto-linked here and visible in the DMS.
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {docs.map((doc) => {
                const Icon = TYPE_ICONS[doc.document_type] ?? FileGenericIcon;
                const latest = doc.versions[0];
                const isSelected = viewing?.id === doc.id;
                return (
                  <li
                    key={doc.id}
                    className={`group flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.04] cursor-pointer ${
                      isSelected ? "bg-brand-500/15" : ""
                    }`}
                    onClick={() => setViewing(doc)}
                  >
                    <Icon className="h-4 w-4 text-surface-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-surface-100 truncate">{doc.name}</div>
                      <div className="text-[11px] text-surface-500 flex flex-wrap gap-x-2">
                        <span>{doc.document_type.toUpperCase()}</span>
                        {latest && <span>v{latest.version_number}</span>}
                        {doc.folder_path && <span>{doc.folder_path}</span>}
                        {doc.links.length > 1 && (
                          <span className="inline-flex items-center gap-0.5">
                            <Link2 className="h-3 w-3" />
                            {doc.links.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      title="Delete"
                      className="hidden group-hover:block text-surface-400 hover:text-red-300 p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(doc);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {viewing && viewerVersion && (
          <div className="lg:w-[55%] lg:min-w-[420px] border-t lg:border-t-0 lg:border-l border-white/10 min-h-[420px]">
            <FileViewer
              version={viewerVersion}
              documentType={viewing.document_type}
              flush
            />
          </div>
        )}
      </div>

      <UploadDocumentModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        virtualSiteId={virtualSiteId}
        defaultFolderPath={defaultFolderPath}
        linkEntityType={entityType}
        linkEntityId={entityId}
        onUploaded={() => reload()}
        title="Upload and link"
      />

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete document?"
        message={
          confirmDelete
            ? `Delete "${confirmDelete.name}" and every uploaded version? This removes the files from S3.`
            : ""
        }
        confirmLabel="Delete"
      />
    </section>
  );
}
